import React, { FunctionComponent } from 'react';

import SummaryCard from 'components/SummaryCard';
import { Text } from 'components/chroma';

import { toCurrencyWithSign } from 'scenes/common/helpers/currency';
import {
  LandlordCoverSubSection,
  LandlordFunnelState,
  LandlordQuoteSubSection,
} from 'scenes/landlord/types';
import { LandlordFullPageProps } from 'scenes/landlord/components/FullPage/types';
import {
  getContentsSumInsured,
  getExtras,
  getCoverType,
  getSelectedBuildingExcess,
  getSelectedContentsExcess,
  hasBuildingCoverType,
  hasContentsCoverType,
  usingEstimatedSumInsured,
  getBuildingSumInsured,
  isDamagedCausedByTenantsExtraSelected,
  isLossOfRentExtraSelected,
  isTenantsRentDefaultExtraSelected,
} from 'scenes/landlord/selectors';
import { availableExtras } from 'scenes/landlord/helpers/OptionalExtras/availableExtras';
import { excessDetails, ExcessName } from 'scenes/landlord/helpers/excess';

const toOptionalExtraDescription = (
  optionalExtra: string,
  funnelState: LandlordFunnelState
): string =>
  availableExtras[getCoverType(funnelState)].find(
    (extra) => extra.code === optionalExtra
  )?.label || optionalExtra;

export const CoverDetailsSummary: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditCoverDetails'
>> = ({ funnelState, onEditQuoteDetails, onEditCoverDetails }) => {
  const extras = getExtras(funnelState);
  return (
    <>
      {hasBuildingCoverType(funnelState) && (
        <SummaryCard
          heading="Building excess"
          onEditClick={() =>
            onEditCoverDetails(LandlordCoverSubSection.BuildingExcess)
          }
        >
          <Text>{`${toCurrencyWithSign(
            getSelectedBuildingExcess(funnelState).amount
          )}`}</Text>
        </SummaryCard>
      )}
      {hasContentsCoverType(funnelState) && (
        <SummaryCard
          heading="Contents excess"
          onEditClick={() =>
            onEditCoverDetails(LandlordCoverSubSection.ContentsExcess)
          }
        >
          <Text>{`${toCurrencyWithSign(
            getSelectedContentsExcess(funnelState).amount
          )}`}</Text>
        </SummaryCard>
      )}
      {isDamagedCausedByTenantsExtraSelected(funnelState) && (
        <SummaryCard
          heading={excessDetails[ExcessName.DamageCausedByTenants].heading}
          onEditClick={() =>
            onEditCoverDetails(LandlordCoverSubSection.OptionalExtras)
          }
        >
          <Text>
            {toCurrencyWithSign(
              excessDetails[ExcessName.DamageCausedByTenants].amount ?? 0
            )}
          </Text>
        </SummaryCard>
      )}
      {isLossOfRentExtraSelected(funnelState) && (
        <SummaryCard
          heading={excessDetails[ExcessName.LossOfRent].heading}
          onEditClick={() =>
            onEditCoverDetails(LandlordCoverSubSection.OptionalExtras)
          }
        >
          <Text>
            {toCurrencyWithSign(
              excessDetails[ExcessName.LossOfRent].amount ?? 0
            )}
          </Text>
        </SummaryCard>
      )}
      {isTenantsRentDefaultExtraSelected(funnelState) && (
        <SummaryCard
          heading={excessDetails[ExcessName.TenantsRentDefault].heading}
          onEditClick={() =>
            onEditCoverDetails(LandlordCoverSubSection.OptionalExtras)
          }
        >
          <Text>
            {toCurrencyWithSign(
              excessDetails[ExcessName.TenantsRentDefault].amount ?? 0
            )}
          </Text>
        </SummaryCard>
      )}

      {hasBuildingCoverType(funnelState) && (
        <SummaryCard
          heading="Building sum insured"
          onEditClick={() =>
            usingEstimatedSumInsured(funnelState)
              ? onEditQuoteDetails(
                  LandlordQuoteSubSection.BuildingSumInsuredChoice
                )
              : onEditQuoteDetails(
                  LandlordQuoteSubSection.BuildingAgreedSumInsured
                )
          }
        >
          <Text>{toCurrencyWithSign(getBuildingSumInsured(funnelState))}</Text>
        </SummaryCard>
      )}

      {hasContentsCoverType(funnelState) && (
        <SummaryCard
          heading="Contents sum insured"
          onEditClick={() =>
            onEditQuoteDetails(LandlordQuoteSubSection.ContentsSumInsured)
          }
        >
          <Text>{`${toCurrencyWithSign(
            getContentsSumInsured(funnelState)
          )}`}</Text>
        </SummaryCard>
      )}

      <SummaryCard
        heading="Optional extras"
        onEditClick={() =>
          onEditCoverDetails(LandlordCoverSubSection.OptionalExtras)
        }
      >
        {extras.length > 0 ? (
          extras.map((optionalExtra) => (
            <Text key={optionalExtra}>
              {toOptionalExtraDescription(optionalExtra, funnelState)}
            </Text>
          ))
        ) : (
          <Text>None</Text>
        )}
      </SummaryCard>
    </>
  );
};

export default CoverDetailsSummary;
